// Photo studio service — AWS Amplify (DynamoDB) for persistence, backend Redis for atomic positions
// Keeps all algorithm fixes: single source of truth for positions, consistent dates, duplicate lock
import { API } from 'aws-amplify';
import { createBadgeSubmission, updateBadgeSubmission } from '../graphql/mutations';
import { listBadgeSubmissions } from '../graphql/queries';

const LOCK_KEY_PREFIX = 'booking_lock:';

// --- Validation ---

const validateEmail = (email) => {
  return email && email.includes('@') && email.endsWith('@amazon.com');
};

const validateSiteCode = (siteCode) => {
  return ['BLR13', 'BLR14', 'BLR18', 'BLR26', 'BLR27'].includes(siteCode);
};

const validateTimeSlot = (timeSlot) => {
  return timeSlot && /^\d{2}:\d{2}-\d{2}:\d{2}$/.test(timeSlot);
};

// Normalize dates to YYYY-MM-DD for consistent comparison
const toDateKey = (dateInput) => {
  const d = new Date(dateInput);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// --- Duplicate check with session lock ---

const checkAndLockDuplicate = async (email, siteCode, dateKey) => {
  const lockKey = `${LOCK_KEY_PREFIX}${email}:${siteCode}:${dateKey}`;
  
  // Use localStorage for cross-tab lock (sessionStorage is per-tab only)
  const existingLock = localStorage.getItem(lockKey);
  if (existingLock) {
    // Lock expires after 30 seconds (in case of abandoned booking)
    const lockAge = Date.now() - parseInt(existingLock);
    if (lockAge < 30000) {
      return { isDuplicate: true, reason: 'Another booking is in progress' };
    }
    // Stale lock — remove it
    localStorage.removeItem(lockKey);
  }
  
  // Check DynamoDB for existing booking
  try {
    let allItems = [];
    let nextToken = null;
    
    do {
      const result = await API.graphql({
        query: listBadgeSubmissions,
        variables: {
          filter: {
            email: { eq: email },
            siteCode: { eq: siteCode },
            status: { ne: 'Cancelled' }
          },
          limit: 1000,
          nextToken
        }
      });
      
      const data = result.data?.listBadgeSubmissions;
      if (data?.items) {
        allItems = allItems.concat(data.items);
        nextToken = data.nextToken;
      } else {
        break;
      }
    } while (nextToken);
    
    const duplicate = allItems.find(apt =>
      apt &&
      apt.email?.toLowerCase().trim() === email &&
      apt.siteCode === siteCode &&
      toDateKey(apt.submissionDate || apt.createdAt) === dateKey &&
      apt.status !== 'Cancelled' && apt.status !== 'Expired'
    );
    
    if (duplicate) {
      return { isDuplicate: true, reason: `You already have a booking for ${siteCode} today. Token: ${duplicate.tokenNumber}` };
    }
  } catch (error) {
    console.warn('Duplicate check via API failed, proceeding:', error.message);
  }
  
  localStorage.setItem(lockKey, Date.now().toString());
  return { isDuplicate: false, lockKey };
};

const releaseLock = (lockKey) => {
  if (lockKey) localStorage.removeItem(lockKey);
};

/**
 * Saves photo studio appointment to DynamoDB via Amplify.
 * Trusts the queuePosition from backend Redis — no second position calculation.
 */
export const savePhotoStudioAppointment = async (formData) => {
  let lockKey = null;
  
  try {
    console.log('📝 Saving appointment to DynamoDB:', formData);
    
    if (!validateEmail(formData.email)) throw new Error('Invalid email address. Must be @amazon.com');
    if (!validateSiteCode(formData.siteCode)) throw new Error('Invalid studio location');
    if (!validateTimeSlot(formData.timeSlot)) throw new Error('Invalid time slot format');
    
    const dateKey = toDateKey(formData.bookingDate || formData.submissionDate || new Date().toISOString());
    
    const dupCheck = await checkAndLockDuplicate(
      formData.email.toLowerCase().trim(),
      formData.siteCode,
      dateKey
    );
    
    if (dupCheck.isDuplicate) throw new Error(dupCheck.reason);
    lockKey = dupCheck.lockKey;
    
    // Build the DynamoDB record — only include fields that exist in the schema
    const appointment = {
      id: formData.id,
      email: formData.email.toLowerCase().trim(),
      siteCode: formData.siteCode,
      formType: 'photoStudio',
      tokenNumber: formData.tokenNumber,
      queuePosition: parseInt(formData.queuePosition) || 1,
      estimatedTime: formData.estimatedTime,
      timeSlot: formData.timeSlot,
      photoTaken: 'No',
      status: 'Pending',
      submissionDate: formData.submissionDate || new Date().toLocaleString()
    };
    
    const result = await API.graphql({
      query: createBadgeSubmission,
      variables: { input: appointment }
    });
    
    console.log('✅ Saved to DynamoDB:', result.data.createBadgeSubmission);

    return {
      success: true,
      message: 'Appointment booked successfully',
      data: result.data.createBadgeSubmission
    };
  } catch (error) {
    console.error('❌ Save failed:', error);
    const msg = error.errors ? error.errors.map(e => e.message).join(', ') : (error.message || 'Failed to save appointment');
    return { success: false, message: msg, error: msg };
  } finally {
    releaseLock(lockKey);
  }
};

/**
 * Retrieves all photo studio appointments from DynamoDB
 */
export const getPhotoStudioAppointments = async () => {
  try {
    console.log('📊 Fetching appointments from DynamoDB...');
    
    let allItems = [];
    let nextToken = null;
    
    do {
      const result = await API.graphql({
        query: listBadgeSubmissions,
        variables: { limit: 1000, nextToken }
      });
      
      const data = result.data?.listBadgeSubmissions;
      if (data?.items) {
        allItems = allItems.concat(data.items);
        nextToken = data.nextToken;
      } else {
        break;
      }
    } while (nextToken);
    
    console.log(`📊 Raw data: ${allItems.length} items`);
    
    // Filter for photo studio appointments only (exclude counters, holidays, etc.)
    const photoData = allItems.filter(item =>
      item && 
      item.formType !== 'counter' && 
      item.formType !== 'holiday' &&
      item.formType !== 'adminAccount' &&
      (item.formType === 'photoStudio' || (item.siteCode && item.tokenNumber && item.email !== 'system@counter.internal'))
    );
    
    // Process data — normalize fields
    const processedData = photoData.map(item => {
      let timeSlot = item.timeSlot;
      let estimatedTime = item.estimatedTime;
      
      // Handle legacy format where estimatedTime contained "time|slot"
      if (item.estimatedTime && item.estimatedTime.includes('|')) {
        const parts = item.estimatedTime.split('|');
        estimatedTime = parts[0];
        if (!timeSlot) timeSlot = parts[1];
      }
      
      return {
        ...item,
        timeSlot,
        estimatedTime,
        submittedAt: item.submittedAt || item.createdAt,
        queuePosition: parseInt(item.queuePosition) || 0,
        email: item.email ? item.email.toLowerCase().trim() : '',
        status: item.status || 'Pending'
      };
    }).filter(item => item.id && item.siteCode);
    
    // Sort newest first
    processedData.sort((a, b) => {
      const dateA = new Date(a.submittedAt || a.createdAt).getTime();
      const dateB = new Date(b.submittedAt || b.createdAt).getTime();
      return dateB - dateA;
    });
    
    console.log(`✅ Final: ${processedData.length} valid appointments`);
    
    return { success: true, data: processedData };
  } catch (error) {
    console.error('❌ Fetch failed:', error);
    return { success: false, data: [], message: error.message || 'Failed to fetch appointments' };
  }
};

/**
 * Updates photo studio appointment status in DynamoDB
 */
export const updatePhotoStudioStatus = async (id, photoData, adminEmail, newStatus) => {
  try {
    if (!id) throw new Error('Appointment ID is required');
    
    const updateData = {
      id,
      updatedAt: new Date().toISOString()
    };
    
    if (adminEmail) updateData.updatedBy = adminEmail.toLowerCase().trim();
    
    if (newStatus !== null && newStatus !== undefined) {
      updateData.status = newStatus;
      if (newStatus === 'Completed') {
        updateData.photoTaken = 'Yes';
      } else if (newStatus === 'Pending' || newStatus === 'In Progress') {
        updateData.photoTaken = 'No';
      }
    }
    
    if (photoData !== null && photoData !== undefined) {
      updateData.photoTaken = photoData;
      if (photoData === 'Yes') updateData.status = 'Completed';
    }

    await API.graphql({
      query: updateBadgeSubmission,
      variables: { input: updateData }
    });
    
    console.log(`✅ Status updated: ${id} -> ${newStatus}`);
    
    return { success: true, message: 'Status updated successfully', data: { id, status: newStatus, updatedBy: adminEmail } };
  } catch (error) {
    console.error('❌ Update failed:', error);
    return { success: false, message: error.message || 'Failed to update status', error: error.message };
  }
};
